import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import DeceasedTable from './table';
import { createSectionByIdSelector } from '../../../../redux/pages/selectors';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { RSOverviewPageModel } from '../../../../api/api.types';
import type { RiskOverviewMonitorFundRow } from '../../../../api/rs-hg/risk/riskOverview.types';
import {
  ReportsListByFund,
  useOverviewQueries,
} from '../../../../api/rs-hg/risk/hooks/queries';
import { sortFundNames } from '../../../../utilities/customSorting';

type DeceasedPageData = {
  overview: RSOverviewPageModel<RiskOverviewMonitorFundRow[]>;
  reports: ReportsListByFund;
};

const Deceased: FC<GeneralOverviewPageRenderedComponentProps> = (props) => {
  const sectionDetails = useSelector(createSectionByIdSelector(props.section));
  const fundTypes = sectionDetails?.fundTypes;
  const selectedRaptorPlatformId = useSelector(raptorPlatformIdSelector);

  const { reportsQuery, overviewQuery } = useOverviewQueries({
    fundTypes,
    positionDate: props.positionDate,
    raptorPlatformId: selectedRaptorPlatformId,
    deceased: true,
  });

  const reportsReady = !reportsQuery.isLoading;

  const pageData = useMemo((): DeceasedPageData | null => {
    if (!overviewQuery.data?.data || !reportsReady) {
      return null;
    }

    const overviewRows = [...overviewQuery.data.data].sort((a, b) =>
      sortFundNames(a.fund_name || '', b.fund_name || ''),
    );

    return {
      overview: {
        ...overviewQuery.data,
        data: overviewRows,
      },
      reports: reportsQuery.data ?? {},
    };
  }, [overviewQuery.data, reportsQuery.data, reportsReady]);

  return (
    <QueryBoundary<DeceasedPageData>
      isLoading={overviewQuery.isLoading || reportsQuery.isLoading}
      isFetching={overviewQuery.isFetching || reportsQuery.isFetching}
      isError={!!overviewQuery.error}
      error={overviewQuery.error}
      data={pageData}
      loadingMessages={['Loading Deceased Data...']}
      errorMessage="No Data Available."
      noDataMessage="No Data Available."
    >
      {({ overview, reports }) => (
        <DeceasedTable
          overviewData={overview}
          reportsList={reports}
          pageId={props.pageId}
        />
      )}
    </QueryBoundary>
  );
};

export default Deceased;
